import { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '@/i18n';
import Calendar from '@/components/Calendar';
import type { CalendarEvent } from '@/components/Calendar';
import UpcomingList from '@/components/UpcomingList';

interface OwnerContext {
  username: string;
  displayName: string;
  avatarUrl: string;
}

type View = 'list' | 'calendar';
interface MonthPos {
  year: number;
  month: number; // 1-based
}

// Only show the loading skeleton once a load has taken this long. Fast loads (the common
// case) finish first and render content directly, so the skeleton never flashes. The
// server-reserved min-h-screen wrapper keeps the height stable during the brief blank window.
const SKELETON_DELAY_MS = 250;

function readInitialParams(): { view: View; pos: MonthPos | null } {
  const p = new URLSearchParams(window.location.search);
  const view: View = p.get('view') === 'calendar' ? 'calendar' : 'list';
  const month = parseInt(p.get('month') ?? '', 10);
  const year = parseInt(p.get('year') ?? '', 10);
  // Bound the year to a sane range so a bad query (e.g. year=50 → JS Date 1950) falls back
  // to the next-trip month instead of rendering a nonsensical empty month.
  const validYear = year >= 2000 && year <= 2100;
  const pos = month >= 1 && month <= 12 && validYear ? { year, month } : null;
  return { view, pos };
}

/** Is the event still upcoming (has not finished yet)? Mirrors the backend `upcoming` filter. */
function isUpcoming(ev: CalendarEvent, todayStart: number): boolean {
  const endStr = ev.end_at ?? ev.start_at;
  if (!endStr) return false;
  return new Date(endStr).getTime() >= todayStart;
}

export default function App({ owner }: { owner: OwnerContext }) {
  const initial = useRef(readInitialParams());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>(initial.current.view);
  const [calPos, setCalPos] = useState<MonthPos | null>(initial.current.pos);

  useEffect(() => {
    const ac = new AbortController();
    // Fetch ALL of the owner's published events (unfiltered → the API returns every month),
    // so the list can span future months and the calendar can navigate freely.
    fetch(`/api/users/${owner.username}/events/`, { signal: ac.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: CalendarEvent[]) => setEvents(data))
      .catch((err) => {
        // Aborted on unmount (e.g. StrictMode dev double-mount) — not a real failure.
        if (err?.name === 'AbortError') return;
        setError(t('Failed to load events'));
      })
      .finally(() => {
        // Don't clear loading for an aborted request — the remounted effect's fetch
        // is still in flight and owns the loading state.
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [owner.username]);

  // Delay the skeleton so a fast fetch swaps straight to content without a flash.
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => setShowSkeleton(true), SKELETON_DELAY_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  const upcoming = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return events
      .filter((ev) => isUpcoming(ev, todayStart))
      .sort((a, b) => new Date(a.start_at ?? 0).getTime() - new Date(b.start_at ?? 0).getTime());
  }, [events]);

  const nextTripMonth: MonthPos | null = useMemo(() => {
    const first = upcoming[0];
    if (!first?.start_at) return null;
    const d = new Date(first.start_at);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  }, [upcoming]);

  // The calendar opens on: an explicit URL month → else the next upcoming trip → else today.
  const calInitial = calPos ?? nextTripMonth;

  // Keep the URL in sync so a shared link restores the view (and month, in calendar view).
  useEffect(() => {
    if (loading) return;
    const p = new URLSearchParams();
    p.set('view', view);
    if (view === 'calendar') {
      const pos = calPos ?? nextTripMonth;
      if (pos) {
        p.set('month', String(pos.month));
        p.set('year', String(pos.year));
      }
    }
    try {
      window.history.replaceState(null, '', `?${p.toString()}`);
    } catch {
      /* null origin (e.g. file://) — ignore */
    }
  }, [view, calPos, nextTripMonth, loading]);

  function handleEventClick(event: CalendarEvent) {
    window.location.href = `/${event.slug}/`;
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <main className="mx-auto max-w-5xl px-5 py-8 sm:px-8 sm:py-10">
        {/* View toggle */}
        <div className="flex items-center justify-between">
          <div className="inline-flex rounded-full bg-gray-100 p-1 text-sm dark:bg-gray-800">
            {(['list', 'calendar'] as View[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`w-24 rounded-full py-1.5 transition-colors sm:w-28 ${
                  view === v
                    ? 'bg-teal-600 font-semibold text-white shadow-sm'
                    : 'font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {v === 'list' ? t('Upcoming') : t('Calendar')}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-500/20 dark:bg-red-500/5">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* List view */}
        {view === 'list' && !error && (
          <div className="mt-6">
            {!loading ? (
              <UpcomingList events={upcoming} />
            ) : showSkeleton ? (
              <ListSkeleton />
            ) : null}
          </div>
        )}

        {/* Calendar view — mount only once loaded so it opens on the next-trip month. */}
        {view === 'calendar' && !error && (
          <div className="mt-6">
            {!loading ? (
              <Calendar
                events={events}
                onEventClick={handleEventClick}
                onMonthChange={(year, month) => setCalPos({ year, month })}
                initialMonth={calInitial?.month}
                initialYear={calInitial?.year}
                nextTripMonth={nextTripMonth}
              />
            ) : showSkeleton ? (
              <div className="h-96 animate-pulse rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950" />
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="flex items-start gap-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-950 sm:p-5"
        >
          <div className="h-14 w-14 shrink-0 rounded-xl bg-gray-200 dark:bg-gray-800 sm:h-16 sm:w-16" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-800" />
            <div className="h-5 w-2/3 rounded bg-gray-200 dark:bg-gray-800" />
            <div className="h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-800" />
          </div>
        </div>
      ))}
    </div>
  );
}
